import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
      return detail
        .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.email, formData.password, formData.name);
      }
      toast.success(isLogin ? 'Login successful!' : 'Registration successful!');
      navigate(from, { replace: true });
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center bg-cream" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md mx-4"
      >
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.12)] border border-charcoal-100 p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-heading font-bold text-charcoal-800 mb-2">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-charcoal-600 font-body">
              {isLogin ? 'Sign in to your account' : 'Join our spiritual community'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid={isLogin ? 'login-form' : 'register-form'}>
            {!isLogin && (
              <div>
                <label className="block text-sm font-body font-medium text-charcoal-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  data-testid="register-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-body font-medium text-charcoal-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                data-testid={isLogin ? 'login-email-input' : 'register-email-input'}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body"
              />
            </div>
            <div>
              <label className="block text-sm font-body font-medium text-charcoal-700 mb-2">
                Password *
              </label>
              <input
                type="password"
                required
                data-testid={isLogin ? 'login-password-input' : 'register-password-input'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid={isLogin ? 'login-submit-button' : 'register-submit-button'}
              className="w-full bg-saffron-600 text-white rounded-full px-8 py-4 font-body font-medium hover:bg-saffron-500 transition-colors shadow-lg disabled:opacity-50"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Sign Up'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setFormData({ email: '', password: '', name: '' });
              }}
              data-testid="toggle-auth-mode-button"
              className="text-saffron-600 font-body font-medium hover:text-saffron-500 transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
